import React from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Separator } from '@/components/ui/separator';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { LoadingButton } from '../../common/LoadingButton';
import type { DRBFMCasePrecheckItem, PrecheckAnswerStatus } from '../../../types/drbfm';

interface DRBFMPrecheckStepProps {
  items: DRBFMCasePrecheckItem[];
  isLoading: boolean;
  onChange: (items: DRBFMCasePrecheckItem[]) => void;
  onSubmit: (items: DRBFMCasePrecheckItem[]) => void | Promise<unknown>;
  onBack: () => void;
}

const statusBadgeVariant = (status: PrecheckAnswerStatus) => {
  if (status === 'answered') return 'success' as const;
  return status === 'unanswered' ? ('outline' as const) : ('secondary' as const);
};

const DRBFMPrecheckStep: React.FC<DRBFMPrecheckStepProps> = ({
  items,
  isLoading,
  onChange,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const statusLabels: Record<PrecheckAnswerStatus, string> = {
    unanswered: t('drbfm.case.answerStatus.unanswered', '未回答'),
    answered: t('drbfm.case.answerStatus.answered', '回答済み'),
    unknown: t('drbfm.case.answerStatus.unknown', '不明'),
    not_applicable: t('drbfm.case.answerStatus.not_applicable', '該当なし'),
  };

  const updateItem = (
    index: number,
    update: Pick<DRBFMCasePrecheckItem, 'answerStatus' | 'answerText'>,
  ) => {
    onChange(items.map((item, currentIndex) => (
      currentIndex === index ? { ...item, ...update } : item
    )));
  };

  const handleAnswerTextChange = (index: number, value: string) => {
    const trimmed = value.trim();
    updateItem(index, {
      answerStatus: trimmed ? 'answered' : 'unanswered',
      answerText: value,
    });
  };

  const handleStatusClick = (index: number, answerStatus: 'unknown' | 'not_applicable') => {
    updateItem(index, {
      answerStatus,
      answerText: null,
    });
  };

  const handleSubmit = async () => {
    await onSubmit(items);
  };
  const hasUnansweredItems = items.some((item) => item.answerStatus === 'unanswered');
  const isSubmitDisabled = isLoading || hasUnansweredItems;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="revy-cjk mb-2 text-xl font-medium leading-[1.5]">
          {t('drbfm.case.precheckTitle', '事前確認')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('drbfm.case.precheckDescription', '不明点を明示してからDRBFM分析へ進みます。')}
        </p>
      </div>

      {items.length === 0 ? (
        <p className="revy-cjk text-base leading-[1.6] text-muted-foreground">
          {t('drbfm.case.noPrecheckItems', '事前確認項目はありません。')}
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {items.map((item, index) => (
            <React.Fragment key={item.id || index}>
              {index > 0 && <Separator />}
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="revy-cjk text-base font-normal leading-[1.5]">
                    {item.question}
                  </h3>
                  <Badge
                    variant={statusBadgeVariant(item.answerStatus)}
                    className="revy-cjk font-normal"
                  >
                    {statusLabels[item.answerStatus]}
                  </Badge>
                </div>

                <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
                  {item.reason}
                </p>

                <Field
                  label={t('drbfm.case.answer', '回答')}
                  help={t('drbfm.case.answerHelper', '入力すると回答済みになります')}
                >
                  {(control) => (
                    <AutoGrowTextarea
                      {...control}
                      rows={2}
                      value={item.answerText || ''}
                      onChange={(event) => handleAnswerTextChange(index, event.target.value)}
                      disabled={isLoading}
                    />
                  )}
                </Field>

                <div className="flex gap-2">
                  <Button
                    variant={item.answerStatus === 'unknown' ? 'default' : 'outline'}
                    onClick={() => handleStatusClick(index, 'unknown')}
                    disabled={isLoading}
                  >
                    <span className="revy-cjk">
                      {t('drbfm.case.answerStatus.unknown', '不明')}
                    </span>
                  </Button>
                  <Button
                    variant={item.answerStatus === 'not_applicable' ? 'default' : 'outline'}
                    onClick={() => handleStatusClick(index, 'not_applicable')}
                    disabled={isLoading}
                  >
                    <span className="revy-cjk">
                      {t('drbfm.case.answerStatus.not_applicable', '該当なし')}
                    </span>
                  </Button>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" disabled={isLoading} onClick={onBack}>
          <span className="revy-cjk">
            {t('drbfm.case.backToChangeReview', '変更点レビューに戻る')}
          </span>
        </Button>
        <LoadingButton
          loading={isLoading}
          loadingText={t('drbfm.case.generatingDrbfm', 'DRBFM生成中')}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          <Sparkles strokeWidth={1.9} />
          <span className="revy-cjk">{t('drbfm.case.generateDrbfm', 'DRBFMを生成')}</span>
        </LoadingButton>
      </div>

      {hasUnansweredItems && (
        <p className="revy-cjk text-sm leading-[1.6] text-destructive">
          {t('drbfm.case.answerRequired', 'すべての確認項目に回答するか、不明または該当なしを選択してください。')}
        </p>
      )}
    </div>
  );
};

export default DRBFMPrecheckStep;
